import React, { Component, Fragment, useEffect, useRef } from 'react';
import { bool, func, string } from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { Form as FinalForm } from 'react-final-form';
import isEqual from 'lodash/isEqual';
import classNames from 'classnames';

import config from '../../config';
import { propTypes } from '../../util/types';
import * as validators from '../../util/validators';
import {
  FieldCheckbox,
  Form,
  FieldSelect,
  FieldTextInput,
  ContactDetails,
  Modal,
  ModalPasswordConfirmation,
  PrimaryButton,
} from '../../components';
import HowDidYouKnowUsSelectField from '../../components/HowDidYouKnowUsSelectField';

import css from './PersonalInfosForm.module.css';

const SyncTaxIdNumberState = (props) => {
  const { form, hasNoTaxIdNumber, taxIdNumber } = props;
  const previousHasNoTaxIdNumber = useRef(hasNoTaxIdNumber);

  useEffect(() => {
    const toggledOn = hasNoTaxIdNumber && !previousHasNoTaxIdNumber.current;

    if (toggledOn) {
      if (taxIdNumber) {
        form.change('taxIdNumber', undefined);
      }
    }

    previousHasNoTaxIdNumber.current = hasNoTaxIdNumber;
  }, [form, hasNoTaxIdNumber, taxIdNumber]);

  return null;
};

class PersonalInfosForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      changeEmail: false,
      showTaxIdInfoModal: false,
      showTaxIdWhereToFindModal: false,
    };
    this.submittedValues = {};
  }

  componentWillUnmount() {
    window.clearTimeout(this.blurTimeoutId);
  }

  render() {
    const { onManageDisableScrolling } = this.props;

    return (
      <FinalForm
        {...this.props}
        render={(fieldRenderProps) => {
          const {
            rootClassName,
            className,
            changeEmailError,
            changeEmailInProgress,
            formId,
            handleSubmit,
            updateInProgress,
            updateProfileError,
            intl,
            initialValues,
            invalid,
            pristine,
            form,
            values,
          } = fieldRenderProps;
          const hasNoTaxIdNumber = !!values.hasNoTaxIdNumber;
          const emailHasChanged = values.email !== initialValues.email;
          const handleOpenModal = (e) => {
            this.setState({ changeEmail: true });
          };
          const firstNameLabel = intl.formatMessage({ id: 'PersonalInfosForm.firstNameLabel' });
          const firstNamePlaceholder = intl.formatMessage({
            id: 'PersonalInfosForm.firstNamePlaceholder',
          });
          const firstNameRequiredMessage = intl.formatMessage({
            id: 'PersonalInfosForm.firstNameRequired',
          });
          const firstNameRequired = validators.required(firstNameRequiredMessage);
          const lastNameLabel = intl.formatMessage({ id: 'PersonalInfosForm.lastNameLabel' });
          const lastNamePlaceholder = intl.formatMessage({
            id: 'PersonalInfosForm.lastNamePlaceholder',
          });
          const lastNameRequiredMessage = intl.formatMessage({
            id: 'PersonalInfosForm.lastNameRequired',
          });
          const lastNameRequired = validators.required(lastNameRequiredMessage);
          const submitError =
            updateProfileError || changeEmailError ? (
              <div className={css.error}>
                <FormattedMessage id="PersonalInfosForm.updateProfileFailed" />
              </div>
            ) : null;
          const classes = classNames(rootClassName || css.root, className);
          const submitInProgress = updateInProgress || changeEmailInProgress;
          const submittedOnce = Object.keys(this.submittedValues).length > 0;
          const submitErrorDueToInvalidForm = submittedOnce &&
            !pristine &&
            invalid &&
            !emailHasChanged && (
              <div className={css.error}>
                <FormattedMessage id="PersonalInfosForm.updateProfileFailedDueToInvalidForm" />
              </div>
            );
          const pristineSinceLastSubmit =
            !invalid &&
            !updateProfileError &&
            !changeEmailError &&
            submittedOnce &&
            isEqual(values, this.submittedValues);
          const submitDisabled = pristine || pristineSinceLastSubmit || submitInProgress;
          const birthdayLabel = intl.formatMessage({ id: 'PersonalInfosForm.birthdayLabel' });
          const birthdayRequiredMessage = intl.formatMessage({
            id: 'PersonalInfosForm.birthdayRequired',
          });
          const birthdayRequired = validators.required(birthdayRequiredMessage);
          const birthPlaceLabel = intl.formatMessage({ id: 'PersonalInfosForm.birthPlaceLabel' });
          const birthPlacePlaceholder = intl.formatMessage({
            id: 'PersonalInfosForm.birthPlacePlaceholder',
          });
          const birthPlaceRequiredMessage = intl.formatMessage({
            id: 'PersonalInfosForm.birthPlaceRequired',
          });
          const birthPlaceRequired = validators.required(birthPlaceRequiredMessage);
          const birthdayMinAge = validators.ageAtLeastSwimmy(
            intl.formatMessage(
              { id: 'PersonalInfosForm.birthdayMinAge' },
              { minAge: config.custom.marketplaceAgeRequirement }
            ),
            config.custom.marketplaceAgeRequirement
          );
          const taxIdNumberLabel = intl.formatMessage({ id: 'UserInfo.TaxIdNumberLabel' });
          const taxIdNumberPlaceholder = intl.formatMessage({
            id: 'UserInfo.TaxIdNumberPlaceholder',
          });
          const TaxIdNumberWhy = intl.formatMessage({ id: 'UserInfo.TaxIdNumberWhy' });
          const taxIdNumberWhere = intl.formatMessage({ id: 'UserInfo.TaxIdNumberWhere' });
          const hasNoTaxIdNumberLabel = intl.formatMessage({
            id: 'UserInfo.TaxIdNumberNoDeclaration',
          });
          const taxIdNumberRequiredMessage = intl.formatMessage({
            id: 'UserInfo.TaxIdNumberRequired',
          });
          const taxIdNumberRequired = validators.required(taxIdNumberRequiredMessage);
          const taxIdNumberValidate = (value, allValues) => {
            const noTaxIdDeclared = !!(allValues && allValues.hasNoTaxIdNumber);
            return noTaxIdDeclared ? undefined : taxIdNumberRequired(value);
          };
          const languageRequiredMessage = intl.formatMessage({
            id: 'PersonalInfosForm.languageRequiredMessage',
          });
          const languagePlaceholder = intl.formatMessage({
            id: 'PersonalInfosForm.languagePlaceholder',
          });
          const languageOptionFr = intl.formatMessage({ id: 'PersonalInfosForm.languageOptionFr' });
          const languageOptionEn = intl.formatMessage({ id: 'PersonalInfosForm.languageOptionEn' });
          const languageOptionEs = intl.formatMessage({ id: 'PersonalInfosForm.languageOptionEs' });
          const languageOptionDe = intl.formatMessage({ id: 'PersonalInfosForm.languageOptionDe' });
          const languageOptionIt = intl.formatMessage({ id: 'PersonalInfosForm.languageOptionIt' });
          const languageOptionFrBe = intl.formatMessage({
            id: 'PersonalInfosForm.languageOptionFrBe',
          });
          const languageLabel = intl.formatMessage({ id: 'PersonalInfosForm.languageLabel' });
          const languageHelper = intl.formatMessage({ id: 'PersonalInfosForm.languageHelper' });
          const modalInfosText = intl.formatMessage({ id: 'PersonalInfosForm.modalInfos' });

          return (
            <Fragment>
              <SyncTaxIdNumberState
                form={form}
                hasNoTaxIdNumber={hasNoTaxIdNumber}
                taxIdNumber={values.taxIdNumber}
              />
              <Form
                className={classes}
                id={formId}
                onSubmit={(e) => {
                  this.submittedValues = values;
                  this.setState({
                    changeEmail: false,
                  });

                  handleSubmit(e);
                }}
              >
                <div className={css.nameContainer}>
                  <FieldTextInput
                    className={css.firstName}
                    type="text"
                    id="firstName"
                    name="firstName"
                    label={firstNameLabel}
                    placeholder={firstNamePlaceholder}
                    validate={firstNameRequired}
                  />
                  <FieldTextInput
                    className={css.lastName}
                    type="text"
                    id="lastName"
                    name="lastName"
                    label={lastNameLabel}
                    placeholder={lastNamePlaceholder}
                    validate={lastNameRequired}
                  />
                </div>
                <FieldTextInput
                  className={css.field}
                  name="birthdayDate"
                  id={'birthdayDate'}
                  label={birthdayLabel}
                  type="date"
                  validate={validators.composeValidators(birthdayRequired, birthdayMinAge)}
                />
                <FieldTextInput
                  className={css.field}
                  name="birthPlace"
                  id="birthPlace"
                  label={birthPlaceLabel}
                  placeholder={birthPlacePlaceholder}
                  validate={birthPlaceRequired}
                />
                <div className={css.contactDetailsContainer}>
                  <ContactDetails />
                </div>
                {this.props.hasListings && (
                  <div className={css.field}>
                    <FieldTextInput
                      id="taxIdNumber"
                      name="taxIdNumber"
                      type="text"
                      label={
                        <span>
                          {taxIdNumberLabel}{' '}
                          <button
                            className={css.inlineInfoButton}
                            type="button"
                            onClick={() => this.setState({ showTaxIdWhereToFindModal: true })}
                          >
                            {taxIdNumberWhere}
                          </button>
                        </span>
                      }
                      placeholder={taxIdNumberPlaceholder}
                      disabled={hasNoTaxIdNumber}
                      validate={taxIdNumberValidate}
                      // validateFields={taxIdNumberValidate && ['hasNoTaxIdNumber']}
                      onUnmount={() => form.change('taxIdNumber', undefined)}
                    />
                    <FieldCheckbox
                      id="hasNoTaxIdNumber"
                      name="hasNoTaxIdNumber"
                      label={hasNoTaxIdNumberLabel}
                      className={css.taxIdNumberCheckbox}
                    />
                    <button
                      className={css.helperLink}
                      type="button"
                      onClick={() => this.setState({ showTaxIdInfoModal: true })}
                    >
                      {TaxIdNumberWhy}
                    </button>
                  </div>
                )}
                {!initialValues.howDidYouKnowUs && (
                  <HowDidYouKnowUsSelectField
                    className={css.field}
                    id="howDidYouKnowUs"
                    name="howDidYouKnowUs"
                    intl={intl}
                  />
                )}
                <div className={css.languageContainer}>
                  <FieldSelect
                    id="language"
                    name="language"
                    label={languageLabel}
                    validate={validators.required(languageRequiredMessage)}
                  >
                    <option value="">{languagePlaceholder}</option>
                    <option value="fr">{languageOptionFr}</option>
                    <option value="fr-BE">{languageOptionFrBe}</option>
                    <option value="en">{languageOptionEn}</option>
                    <option value="es">{languageOptionEs}</option>
                    <option value="de">{languageOptionDe}</option>
                    <option value="it">{languageOptionIt}</option>
                  </FieldSelect>
                  <p className={css.helper}>{languageHelper}</p>
                </div>
                {submitError}
                {submitErrorDueToInvalidForm}
                <div className={css.buttonContainer}>
                  <PrimaryButton
                    className={css.submitButton}
                    type={emailHasChanged ? 'button' : 'submit'}
                    inProgress={submitInProgress}
                    disabled={submitDisabled}
                    ready={pristineSinceLastSubmit}
                    {...(emailHasChanged && {
                      onClick: handleOpenModal,
                    })}
                  >
                    <FormattedMessage id="PersonalInfosForm.saveChanges" />
                  </PrimaryButton>
                </div>
              </Form>
              <ModalPasswordConfirmation
                infosText={modalInfosText}
                isOpen={this.state.changeEmail}
                onClose={() => this.setState({ changeEmail: false })}
                onManageDisableScrolling={onManageDisableScrolling}
                submitButtonProps={{
                  inProgress: submitInProgress,
                  disabled: submitDisabled,
                  ready: pristineSinceLastSubmit,
                  form: formId,
                }}
              />
              <Modal
                id="PersonalInfosForm.taxIdInfoModal"
                isOpen={this.state.showTaxIdInfoModal}
                onClose={() => this.setState({ showTaxIdInfoModal: false })}
                onManageDisableScrolling={onManageDisableScrolling}
              >
                <p>
                  Swimmy déclare chaque année aux impôts vos revenus gagnés sur la plateforme. Les
                  impôts confrontent ce montant avec votre déclaration.
                </p>
                <p>
                  Toute plateforme est dans l’obligation de télétransmettre aux impôts l’ensemble
                  des revenus générés par les locations afin que votre déclaration de revenus soit
                  pré-remplie, dans le cas où vous seriez soumis à une fiscalité.
                </p>
                <p>
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://www.impots.gouv.fr/particulier/questions/comment-declarer-mes-revenus-dactivites-annexes-telles-que-le-co-voiturage-la"
                  >
                    Plus de détails sur nos obligations ?
                  </a>
                </p>
              </Modal>
              <Modal
                id="PersonalInfosForm.taxIdWhereToFindModal"
                isOpen={this.state.showTaxIdWhereToFindModal}
                onClose={() => this.setState({ showTaxIdWhereToFindModal: false })}
                onManageDisableScrolling={onManageDisableScrolling}
              >
                <p>
                  Votre numéro fiscal à 13 chiffres est l'identifiant unique que vous utilisez pour
                  toutes vos démarches fiscales. Il figure en haut de la première page de votre
                  dernière déclaration de revenus.
                </p>
              </Modal>
            </Fragment>
          );
        }}
      />
    );
  }
}

PersonalInfosForm.defaultProps = {
  rootClassName: null,
  className: null,
  updateProfileError: null,
  updateProfileReady: false,
};

PersonalInfosForm.propTypes = {
  rootClassName: string,
  className: string,
  onManageDisableScrolling: func.isRequired,
  updateInProgress: bool.isRequired,
  updateProfileError: propTypes.error,
  updateProfileReady: bool,
  intl: intlShape.isRequired,
};

export default injectIntl(PersonalInfosForm);
